import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, Chip, Pagination, CircularProgress, TextField,
  InputAdornment, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, MenuItem, Grid, Alert, Tab, Tabs, Stack, Divider,
  LinearProgress, IconButton, Tooltip, FormGroup, FormControlLabel, Switch, Paper,
} from '@mui/material';
import { Search, Add, Visibility, PersonOff, CheckCircle, Block, Refresh, Login, Tune } from '@mui/icons-material';
import { useState, useEffect, useCallback } from 'react';
import api from '../../api/axios';

const fmt = (n) => new Intl.NumberFormat('en-PK').format(Math.round(n || 0));
const statusColors = { active: 'success', trial: 'info', suspended: 'error', expired: 'default' };

function UsageBar({ label, used, max }) {
  const pct = max > 0 ? Math.min(100, (used / max) * 100) : 0;
  return (
    <Box mb={1}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="caption">{label}</Typography>
        <Typography variant="caption">{used} / {max > 9000 ? '∞' : max}</Typography>
      </Box>
      <LinearProgress variant="determinate" value={pct} color={pct > 90 ? 'error' : pct > 70 ? 'warning' : 'primary'} sx={{ height: 6, borderRadius: 3 }} />
    </Box>
  );
}

// Available modules with labels and descriptions
const MODULE_LIST = [
  { key: 'medical',  label: 'Medical / Pharmacy', desc: 'Expiry management, medicine tracking, Medical Store page' },
  { key: 'garment',  label: 'Garment / Factory',  desc: 'Production orders, raw materials, factory workflow' },
  { key: 'clothing', label: 'Clothing / Retail',  desc: 'Standard clothing retail — included by default' },
  { key: 'wholesale',label: 'Wholesale Pricing',  desc: 'Wholesale price column and bulk order discounts' },
  { key: 'loyalty',  label: 'Loyalty Points',     desc: 'Customer loyalty points and rewards system' },
];

function TenantDetailDialog({ tenantId, open, onClose, onUpdated }) {
  const [detail, setDetail] = useState(null);
  const [plans, setPlans] = useState([]);
  const [planId, setPlanId] = useState('');
  const [months, setMonths] = useState(1);
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const [msg, setMsg] = useState('');
  const [tab, setTab] = useState(0);
  const [modules, setModules] = useState({});
  const [savingModules, setSavingModules] = useState(false);

  useEffect(() => {
    if (open && tenantId) {
      setDetail(null); setMsg(''); setTab(0);
      api.get(`/superadmin/tenants/${tenantId}`).then(r => {
        const d = r.data.data;
        setDetail(d);
        setPlanId(d.subscription_plan_id || '');
        // Parse modules JSON
        let mods = {};
        try { mods = typeof d.modules === 'string' ? JSON.parse(d.modules || '{}') : (d.modules || {}); } catch {}
        setModules(mods);
      });
      api.get('/superadmin/plans').then(r => setPlans(r.data.data));
    }
  }, [open, tenantId]);

  const changeStatus = async (status, reason) => {
    await api.patch(`/superadmin/tenants/${tenantId}/status`, { status, reason });
    setDetail(d => ({ ...d, status }));
    onUpdated();
    setMsg(`Status changed to ${status}`);
  };

  const saveModules = async () => {
    setSavingModules(true);
    try {
      await api.put(`/superadmin/tenants/${tenantId}`, {
        name: detail.name, email: detail.email, phone: detail.phone,
        owner_name: detail.owner_name, city: detail.city,
        business_type: detail.business_type, modules,
      });
      onUpdated();
      setMsg('Modules updated successfully! Client must re-login to see changes.');
    } catch(e) { setMsg('Error: ' + (e.response?.data?.message || 'Failed')); }
    setSavingModules(false);
  };

  const assignPlan = async () => {
    try {
      await api.patch(`/superadmin/tenants/${tenantId}/plan`, { plan_id: planId, months, billing_period: billingPeriod });
      onUpdated();
      setMsg(`Plan assigned for ${billingPeriod === 'yearly' ? '12' : months} month(s)`);
    } catch(e) { setMsg('Error: ' + (e.response?.data?.message || 'Failed')); }
  };

  const impersonate = async () => {
    try {
      const { data } = await api.post(`/superadmin/tenants/${tenantId}/impersonate`);
      const current = JSON.parse(localStorage.getItem('pos_auth') || '{}');
      localStorage.setItem('pos_auth_superadmin_backup', JSON.stringify(current));
      localStorage.setItem('pos_auth', JSON.stringify({ accessToken: data.data.token, user: data.data.user }));
      window.location.href = '/';
    } catch(e) { alert('Impersonation failed: ' + (e.response?.data?.message || 'Error')); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>Tenant: {detail?.name}</span>
        {detail && <Chip label={detail.status} color={statusColors[detail.status] || 'default'} />}
      </DialogTitle>
      <DialogContent dividers>
        {!detail ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box> : (
          <>
            {msg && <Alert severity={msg.startsWith('Error') ? 'error' : 'success'} sx={{ mb: 2 }} onClose={() => setMsg('')}>{msg}</Alert>}
            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }} variant="scrollable" scrollButtons="auto">
              <Tab label="Overview" />
              <Tab label={`Users (${detail.users?.length || 0})`} />
              <Tab label={`Invoices (${detail.invoices?.length || 0})`} />
              <Tab label="Usage" />
              <Tab label="Domains" />
              <Tab label="Activity" />
              <Tab label="Modules" icon={<Tune fontSize="small" />} iconPosition="start" />
            </Tabs>

            {tab === 0 && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" fontWeight={600} mb={1}>Business Info</Typography>
                  {[['Name', detail.name], ['Email', detail.email], ['Phone', detail.phone], ['Owner', detail.owner_name], ['CNIC', detail.cnic], ['City', detail.city], ['Business Type', detail.business_type], ['Plan', detail.plan_name || '—'], ['Joined', new Date(detail.created_at).toLocaleDateString('en-GB')]].map(([k, v]) => (
                    <Box key={k} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">{k}</Typography>
                      <Typography variant="body2" fontWeight={500}>{v || '—'}</Typography>
                    </Box>
                  ))}
                  {detail.subscription_ends_at && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">Expires</Typography>
                      <Typography variant="body2" fontWeight={500} color={detail.days_left < 7 ? 'error.main' : 'inherit'}>
                        {new Date(detail.subscription_ends_at).toLocaleDateString('en-GB')} ({detail.days_left}d left)
                      </Typography>
                    </Box>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" fontWeight={600} mb={1}>Assign Plan</Typography>
                  <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                    <TextField select size="small" label="Plan" value={planId} onChange={e => setPlanId(e.target.value)} sx={{ flex: 2, minWidth: 140 }}>
                      {plans.map(p => <MenuItem key={p.id} value={p.id}>{p.name} — Rs.{fmt(p.price_monthly)}/mo</MenuItem>)}
                    </TextField>
                    <TextField select size="small" label="Period" value={billingPeriod} onChange={e => setBillingPeriod(e.target.value)} sx={{ width: 110 }}>
                      <MenuItem value="monthly">Monthly</MenuItem>
                      <MenuItem value="yearly">Yearly</MenuItem>
                    </TextField>
                    {billingPeriod === 'monthly' && (
                      <TextField size="small" label="Months" type="number" value={months} onChange={e => setMonths(e.target.value)} sx={{ width: 80 }} inputProps={{ min: 1, max: 24 }} />
                    )}
                    <Button variant="contained" size="small" onClick={assignPlan} disabled={!planId}>Assign</Button>
                  </Box>

                  <Typography variant="subtitle2" fontWeight={600} mb={1}>Change Status</Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" mb={2}>
                    {['active', 'suspended', 'trial', 'expired'].filter(s => s !== detail.status).map(s => (
                      <Button key={s} size="small" variant="outlined"
                        color={s === 'suspended' ? 'error' : s === 'active' ? 'success' : 'inherit'}
                        onClick={() => changeStatus(s)} sx={{ textTransform: 'capitalize' }}>
                        {s}
                      </Button>
                    ))}
                  </Stack>

                  <Button variant="outlined" startIcon={<Login />} onClick={impersonate} size="small" color="warning">
                    Impersonate (Login as Owner)
                  </Button>
                </Grid>
              </Grid>
            )}

            {tab === 1 && (
              <Table size="small">
                <TableHead><TableRow>
                  <TableCell>Name</TableCell><TableCell>Email</TableCell><TableCell>Role</TableCell><TableCell>Last Login</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {detail.users?.map(u => (
                    <TableRow key={u.id} hover>
                      <TableCell>{u.name}</TableCell>
                      <TableCell>{u.email}</TableCell>
                      <TableCell><Chip label={u.role} size="small" /></TableCell>
                      <TableCell><Typography variant="caption">{u.last_login_at ? new Date(u.last_login_at).toLocaleString('en-PK') : 'Never'}</Typography></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}

            {tab === 2 && (
              <Table size="small">
                <TableHead><TableRow>
                  <TableCell>Invoice #</TableCell><TableCell>Type</TableCell><TableCell align="right">Amount</TableCell><TableCell>Status</TableCell><TableCell>Date</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {detail.invoices?.map(inv => (
                    <TableRow key={inv.id} hover>
                      <TableCell>{inv.invoice_number}</TableCell>
                      <TableCell>{inv.type}</TableCell>
                      <TableCell align="right">Rs.{fmt(inv.total)}</TableCell>
                      <TableCell><Chip label={inv.status} size="small" color={inv.status === 'paid' ? 'success' : inv.status === 'pending' ? 'warning' : 'error'} /></TableCell>
                      <TableCell><Typography variant="caption">{new Date(inv.created_at).toLocaleDateString('en-GB')}</Typography></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}

            {tab === 3 && detail.usage && (
              <Box sx={{ maxWidth: 500 }}>
                <UsageBar label="Users" used={detail.usage.users} max={detail.max_users || 0} />
                <UsageBar label="Branches" used={detail.usage.branches} max={detail.max_branches || 0} />
                <UsageBar label="Products" used={detail.usage.products} max={detail.max_products || 0} />
                <UsageBar label="Customers" used={detail.usage.customers} max={detail.max_customers || 0} />
                <Box mt={2}><Typography variant="body2" color="text.secondary">Total Sales: <strong>{fmt(detail.usage.sales)}</strong></Typography></Box>
              </Box>
            )}

            {tab === 4 && (
              <Box>
                <Typography variant="body2" color="text.secondary" mb={2}>Custom domains for this tenant</Typography>
                {detail.domains?.length === 0 && <Typography variant="body2">No domains configured.</Typography>}
                {detail.domains?.map(d => (
                  <Box key={d.id} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 1 }}>
                    <Typography variant="body2" fontWeight={600}>{d.domain}</Typography>
                    <Stack direction="row" spacing={1} mt={0.5}>
                      <Chip label={d.type} size="small" />
                      <Chip label={d.verification_status} size="small" color={d.verification_status === 'verified' ? 'success' : 'warning'} />
                      <Chip label={`SSL: ${d.ssl_status}`} size="small" />
                    </Stack>
                  </Box>
                ))}
              </Box>
            )}

            {tab === 5 && (
              <Box>
                {detail.activity?.map((a, i) => (
                  <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Box>
                      <Typography variant="body2">{a.description}</Typography>
                      <Typography variant="caption" color="text.secondary">{a.user_name} — {a.action}</Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">{new Date(a.created_at).toLocaleString('en-PK')}</Typography>
                  </Box>
                ))}
              </Box>
            )}

            {tab === 6 && (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={700}>Module Access Control</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Enable or disable specific modules for <strong>{detail.name}</strong>. Changes take effect after client re-logs in.
                    </Typography>
                  </Box>
                  <Button variant="contained" startIcon={<Tune />} onClick={saveModules} disabled={savingModules} size="small">
                    {savingModules ? 'Saving…' : 'Save Modules'}
                  </Button>
                </Box>

                <Grid container spacing={2}>
                  {MODULE_LIST.map(mod => (
                    <Grid item xs={12} sm={6} key={mod.key}>
                      <Paper variant="outlined" sx={{
                        p: 2, borderRadius: 2,
                        borderColor: modules[mod.key] ? 'primary.main' : 'divider',
                        borderWidth: modules[mod.key] ? 2 : 1,
                        bgcolor: modules[mod.key] ? 'primary.50' : 'background.paper',
                        transition: 'all 0.2s',
                      }}>
                        <FormGroup>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={!!modules[mod.key]}
                                onChange={e => setModules(m => ({ ...m, [mod.key]: e.target.checked }))}
                                color="primary"
                              />
                            }
                            label={
                              <Box>
                                <Typography variant="body2" fontWeight={700}>{mod.label}</Typography>
                                <Typography variant="caption" color="text.secondary">{mod.desc}</Typography>
                              </Box>
                            }
                            labelPlacement="end"
                          />
                        </FormGroup>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>

                <Alert severity="info" sx={{ mt: 3 }}>
                  <strong>How it works:</strong> When a module is <strong>ON</strong>, that section appears in the client's sidebar.
                  When <strong>OFF</strong>, the menu item is hidden. The client must <strong>log out and log back in</strong> for changes to apply.
                </Alert>
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

function CreateTenantDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState({ name: '', email: '', owner_name: '', password: '', phone: '', city: '', business_type: 'retail' });
  const [plans, setPlans] = useState([]);
  const [planId, setPlanId] = useState('');
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      api.get('/superadmin/plans').then(r => setPlans(r.data.data));
      setErr('');
    }
  }, [open]);

  const submit = async () => {
    setSaving(true); setErr('');
    try {
      await api.post('/superadmin/tenants', { ...form, plan_id: planId || undefined });
      onCreated();
      onClose();
      setForm({ name: '', email: '', owner_name: '', password: '', phone: '', city: '', business_type: 'retail' });
    } catch(e) { setErr(e.response?.data?.message || 'Failed to create tenant'); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create New Tenant</DialogTitle>
      <DialogContent dividers>
        {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
        <Grid container spacing={2}>
          {[['name', 'Business Name'], ['email', 'Email'], ['owner_name', 'Owner Name'], ['password', 'Password'], ['phone', 'Phone'], ['city', 'City']].map(([field, label]) => (
            <Grid item xs={12} sm={6} key={field}>
              <TextField fullWidth size="small" label={label} type={field === 'password' ? 'password' : 'text'}
                value={form[field]} onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))} required={['name','email','owner_name','password'].includes(field)} />
            </Grid>
          ))}
          <Grid item xs={12} sm={6}>
            <TextField select fullWidth size="small" label="Business Type" value={form.business_type} onChange={e => setForm(f => ({ ...f, business_type: e.target.value }))}>
              {['retail','medical','garment','restaurant','general'].map(t => <MenuItem key={t} value={t} sx={{ textTransform: 'capitalize' }}>{t}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField select fullWidth size="small" label="Subscription Plan (optional)" value={planId} onChange={e => setPlanId(e.target.value)}>
              <MenuItem value="">Trial (no plan)</MenuItem>
              {plans.map(p => <MenuItem key={p.id} value={p.id}>{p.name} — Rs.{fmt(p.price_monthly)}/mo</MenuItem>)}
            </TextField>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={saving}>{saving ? 'Creating...' : 'Create Tenant'}</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function TenantsPage() {
  const [tenants, setTenants] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/superadmin/tenants', { params: { page, limit: 15, search: search || undefined, status: statusFilter || undefined } });
      setTenants(data.data); setTotal(data.pagination?.total || data.total || 0);
    } catch(e) { console.error(e); }
    setLoading(false);
  }, [page, search, statusFilter]);

  useEffect(() => { load(); }, [load]);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Manage Tenants</Typography>
          <Typography variant="body2" color="text.secondary">{total} total clients</Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={() => setCreateOpen(true)}>Add Tenant</Button>
      </Box>

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField placeholder="Search by name, email, phone..." size="small" value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
              sx={{ width: 300 }} />
            <TextField select size="small" label="Status" value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }} sx={{ width: 140 }}>
              <MenuItem value="">All Status</MenuItem>
              {['active','trial','suspended','expired'].map(s => <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>{s}</MenuItem>)}
            </TextField>
            <IconButton onClick={load}><Refresh /></IconButton>
          </Box>

          {loading ? <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box> : (
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell>Business</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Plan</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Revenue</TableCell>
                  <TableCell align="right">Sales</TableCell>
                  <TableCell align="right">Users</TableCell>
                  <TableCell>Joined</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tenants.map(t => (
                  <TableRow key={t.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{t.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{t.email}</Typography>
                    </TableCell>
                    <TableCell><Chip label={t.business_type || 'general'} size="small" variant="outlined" sx={{ textTransform: 'capitalize' }} /></TableCell>
                    <TableCell><Typography variant="body2">{t.plan_name || <span style={{ color: '#9ca3af' }}>None</span>}</Typography></TableCell>
                    <TableCell><Chip label={t.status} size="small" color={statusColors[t.status] || 'default'} /></TableCell>
                    <TableCell align="right"><Typography variant="body2" fontWeight={600}>Rs.{fmt(t.total_paid)}</Typography></TableCell>
                    <TableCell align="right">{t.sale_count || 0}</TableCell>
                    <TableCell align="right">{t.user_count || 0}</TableCell>
                    <TableCell><Typography variant="caption">{new Date(t.created_at).toLocaleDateString('en-GB')}</Typography></TableCell>
                    <TableCell>
                      <Button size="small" startIcon={<Visibility />} onClick={() => setSelectedId(t.id)}>View</Button>
                    </TableCell>
                  </TableRow>
                ))}
                {tenants.length === 0 && (
                  <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4 }}>No tenants found</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          )}

          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
            <Pagination count={Math.ceil(total / 15)} page={page} onChange={(_, v) => setPage(v)} color="primary" />
          </Box>
        </CardContent>
      </Card>

      <TenantDetailDialog tenantId={selectedId} open={!!selectedId} onClose={() => setSelectedId(null)} onUpdated={load} />
      <CreateTenantDialog open={createOpen} onClose={() => setCreateOpen(false)} onCreated={load} />
    </Box>
  );
}
